import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";

const CHOICE = {
  confidence: ["Yes, a lot", "Somewhat", "Not really"],
  pacing: ["Too fast", "About right", "Too slow"],
  unsure: ["Never", "Once or twice", "Often"],
  valuable: ["1.1 Welcome", "1.2 Navigating", "1.3 Mindset", "1.4 Safety", "1.5 Journey"],
};

const Choice = ({ label, name, value, onChange, options, testid }) => (
  <div data-testid={testid}>
    <p className="text-sm font-medium mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          data-testid={`${testid}-${o}`}
          className={`px-3 py-1.5 rounded-sm border text-xs transition-colors ${
            value === o ? "border-primary/60 bg-primary/10 text-primary" : "border-border/70 bg-secondary/30 text-muted-foreground hover:text-foreground"}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);

export const GoldenFiveSurvey = ({ open, onClose }) => {
  const [a, setA] = useState({});
  const [confusing, setConfusing] = useState("");
  const [improve, setImprove] = useState("");
  const [futureValue, setFutureValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (v) => setA((s) => ({ ...s, [k]: v }));

  const finish = () => { localStorage.setItem("da_gf_survey_done", "1"); onClose(); };

  const submit = async () => {
    setSubmitting(true);
    const answers = { ...a, confusing_lessons: confusing, one_improvement: improve, future_value: futureValue };
    try {
      await api.post("/feedback", {
        category: "curriculum", type: "Golden Five onboarding survey",
        message: improve.trim() || "Golden Five onboarding survey completed",
        context: JSON.stringify(answers), source: "golden_five_survey",
      });
      toast.success("Thank you — this shapes the academy.");
      finish();
    } catch (e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="golden-five-survey">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Rocket className="h-4 w-4 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">You Completed the Golden Five</span>
          </div>
          <DialogTitle className="font-display text-2xl">Help us improve DronAcademy</DialogTitle>
          <DialogDescription>You've finished the first five lessons. We'd love to know how your experience felt — it takes under a minute.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <Choice testid="survey-confidence" label="Did the course increase your confidence?" options={CHOICE.confidence} value={a.confidence} onChange={set("confidence")} />
          <Choice testid="survey-pacing" label="Was the pacing…" options={CHOICE.pacing} value={a.pacing} onChange={set("pacing")} />
          <Choice testid="survey-unsure" label="Did you ever feel unsure what to do next?" options={CHOICE.unsure} value={a.unsure} onChange={set("unsure")} />
          <Choice testid="survey-valuable" label="Which lesson was the most valuable?" options={CHOICE.valuable} value={a.valuable} onChange={set("valuable")} />
          <div>
            <p className="text-sm font-medium mb-2">Was any lesson confusing? <span className="text-muted-foreground font-normal">(optional)</span></p>
            <Textarea value={confusing} onChange={(e) => setConfusing(e.target.value)} data-testid="survey-confusing"
              placeholder="If so, which one and why?" className="bg-secondary/40 border-border min-h-[70px]" />
          </div>
          <div>
            <p className="text-sm font-medium mb-2">What's one thing you'd improve?</p>
            <Textarea value={improve} onChange={(e) => setImprove(e.target.value)} data-testid="survey-improve"
              placeholder="One idea to make it better..." className="bg-secondary/40 border-border min-h-[70px]" />
          </div>
          <div>
            <p className="text-sm font-medium mb-2">What would make DronAcademy even more valuable after this course? <span className="text-muted-foreground font-normal">(optional)</span></p>
            <Textarea value={futureValue} onChange={(e) => setFutureValue(e.target.value)} data-testid="survey-future-value"
              placeholder="In your own words..." className="bg-secondary/40 border-border min-h-[70px]" />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="ghost" onClick={finish} data-testid="survey-skip" className="text-muted-foreground">Skip for now</Button>
          <Button onClick={submit} disabled={submitting} data-testid="survey-submit"
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold">
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit feedback
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
